// ─── Password Reset Confirm Page (ocn-S281-P0) ───────────────────
// Handles ?page=password-reset-confirm&token=X&email=Y
// Reads token + email from URL, lets user set a new password.

import React, { useState, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || '';

type ResetState = 'form' | 'loading' | 'success' | 'error';

export function PasswordResetConfirm({ onDone }: { onDone: () => void }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<ResetState>('form');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setState('loading');
    try {
      const res = await fetch(`${API_URL}/api/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password }),
      });
      const data = await res.json().catch(() => ({})) as Record<string, unknown>;
      if (!res.ok) {
        const msg = data.error === 'invalid_or_expired'
          ? 'This reset link has expired or already been used.'
          : data.error === 'password_too_weak'
            ? 'Password must be at least 12 chars with uppercase, lowercase, number, and symbol.'
            : 'Reset failed. Please try again.';
        setError(msg);
        setState('form');
        return;
      }
      setState('success');
    } catch {
      setError('Network error. Please try again.');
      setState('form');
    }
  }, [token, email, password, confirm]);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '11px 14px',
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 7, color: 'var(--text)', fontSize: 14,
    fontFamily: 'var(--font-mono)', outline: 'none',
  };

  if (!token || !email) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 400, padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 14 }}>
          Invalid reset link. <button type="button" onClick={onDone} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orange)', textDecoration: 'underline', fontSize: 14 }}>Return to sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg)' }}>
      <div style={{ width: 400, background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 32, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)', fontSize: 18, fontWeight: 600 }}>Set new password</div>
        {state === 'success' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', textAlign: 'center' }}>
            <div style={{ color: 'var(--green)', fontSize: 14, fontFamily: 'var(--font-mono)' }}>✓ Password updated successfully.</div>
            <button type="button" onClick={onDone} style={{ background: 'var(--orange)', color: '#0B0B1A', border: 'none', borderRadius: 8, padding: '11px 24px', fontWeight: 600, fontFamily: 'var(--font-mono)', cursor: 'pointer', fontSize: 14 }}>
              Sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {error && (
              <div style={{ background: 'var(--red-dim)', color: 'var(--red)', borderRadius: 6, padding: '10px 14px', fontSize: 13, fontFamily: 'var(--font-mono)' }}>{error}</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>New password</label>
              <input type="password" value={password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)} placeholder="Min 12 chars with uppercase, number, symbol" required minLength={12} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>Confirm password</label>
              <input type="password" value={confirm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)} placeholder="Repeat password" required style={inputStyle} />
            </div>
            <button type="submit" disabled={state === 'loading'} style={{ background: 'var(--orange)', color: '#0B0B1A', border: 'none', borderRadius: 8, padding: '13px', fontWeight: 600, fontFamily: 'var(--font-mono)', cursor: state === 'loading' ? 'not-allowed' : 'pointer', fontSize: 14, opacity: state === 'loading' ? 0.7 : 1 }}>
              {state === 'loading' ? 'Updating…' : 'Update password'}
            </button>
            <button type="button" onClick={onDone} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', textDecoration: 'underline' }}>Cancel</button>
          </form>
        )}
      </div>
    </div>
  );
}
